import moment from "moment";
import { alpha } from '@mui/material/styles';
import {
  STANDARD_DEBOUNCE_DELAY,
  MONTH_LIST,
  MONTH_MAP,
  MONTH_NUM_MAP,
  REGULAR_DATE,
  RETIREMENT_DATE,
  MORTALITY_DATE, MONTH_LABEL_MAP,
} from "./constants";
import {
  isDateFurtherThanLifeExpectancy,
  isDatePriorToTodaysMonth,
} from "./validation";

export const getClientDisplayNameById = (clientId, clientInformation) => {
  if (clientId == null || clientId === 0) return "Household";
  if (clientInformation.client.id === clientId)
    return `${clientInformation.client.firstName} ${clientInformation.client.lastName}`;
  if (clientInformation.coClient.id === clientId)
    return `${clientInformation.coClient.firstName} ${clientInformation.coClient.lastName}`;

  const familyMember = clientInformation.family.find((member) => {
    return member.id === clientId;
  });

  if (!familyMember) {
    throw `Data Issue: clientId ${clientId} does not match any existing client, coClient, or family member for this profile`;
  }

  return `${familyMember.firstName} ${familyMember.lastName}`;
};

export const getSelectedClient = (appliesTo, clientInformation) => {
  const appliesToId = appliesTo.id;

  const clientId = clientInformation.client.id;
  if (appliesToId === clientId || appliesToId === 0)
    return clientInformation.client;

  const coClientId = clientInformation.coClient.id;
  if (appliesToId === coClientId) return clientInformation.coClient;

  return clientInformation.family.find((item) => {
    return item.id === appliesToId;
  });
};

export const debounce = (func, delay) => {
  let debounceTimer = null;

  if (!delay) {
    delay = STANDARD_DEBOUNCE_DELAY;
  }

  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
};

export const appendTimestamp = (url) => {
  let now = new Date();
  return url.includes("?")
    ? `${url}&timestamp=${now.getTime().toString()}`
    : `${url}?timestamp=${now.getTime().toString()}`;
};

export const appendListQuery = (url, listQuery) => {
  return `${url}&filter=${
    listQuery.filter != null ? listQuery.filter : ""
  }&skip=${listQuery.skip != null ? listQuery.skip : ""}&take=${
    listQuery.take != null ? listQuery.take : ""
  }&sortField=${
    listQuery.sortField != null ? listQuery.sortField : ""
  }&sortOrder=${listQuery.sortOrder != null ? listQuery.sortOrder : ""}`;
};

export const scrollToRef = (ref) => {
  window.scrollTo(0, ref.current.offsetTop);
};

export const scrollToElement = (id) => {
  setTimeout(() => {
    document.getElementById(id).scrollIntoView(true);
    window.scrollBy(0, -100);
  }, 300);
};

export const uuid = () => {
  let s = [];
  const hexDigits = "0123456789abcdef";
  for (let i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-";

  return s.join("");
};

export const truncateContent = (content, maxLength) => {
  if (content == null) return "";
  return content.length <= maxLength
    ? content
    : content.substring(0, maxLength) + "...";
};

export const getAge = (dateString) => {
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const calculateAnnualizedReturn = (returnRate, risk) => {
  return returnRate - 0.5 * Math.pow(risk, 2);
};

export const calculateAverageReturn = (annualizedReturn, risk) => {
  return annualizedReturn + 0.5 * Math.pow(risk, 2);
};

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const displayCurrencyNoDecimals = (value) => {
  return currencyFormatter.format(Math.round(value)).replace(".00", "");
};

export const monthToInteger = (month) => {
  let monthIndex;
  if (Number.isInteger(month)) {
    monthIndex = month;
  } else {
    monthIndex = MONTH_LIST.findIndex((m) => {
      return m === month;
    }) + 1;
  }
  if (monthIndex < 10) monthIndex = "0" + monthIndex;
  return monthIndex.toString();
};

export const convertKeyDateToAge = (keyDate) => {
  return keyDate.age;
};

export const convertKeyDateToDate = (keyDate) => {
  let month = monthToInteger(keyDate.month);
  return moment(`01-${month}-${keyDate.year}`, "DD-MM-YYYY");
};

export const convertKeyDateToAgeByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let year = keyDate.year;
  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  if (keyDate.keyDateType.key !== "MANUAL_DATE") {
    year += keyDate.relativeAge + adjustedYear;
  }

  let futureDate = moment(
    `${MONTH_NUM_MAP[keyDate.month]}-01-${year}`,
    "MM-DD-YYYY"
  );
  let age = Math.abs(futureDate.diff(dob, "years"));
  switch (keyDate.keyDateType.key) {
    case "RETIREMENT_AGE":
      return client.retirementAge;
    case "LIFE_EXPECTANCY":
      return client.lifeExpectancyAge;
    default:
      return age;
  }
};

export const convertKeyDateToYearByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let age = keyDate.age;

  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;

  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  return dob.add(age + adjustedYear, "years").year();
};

export const convertYearToAgeByClient = (year, client, month) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  const day = month === "Dec" ? 31 : 1;

  let futureDate = moment(
    `${MONTH_NUM_MAP[month]}-${dob.date()}-${parseInt(year)}`,
    "MM-DD-YYYY"
  );
  const age = futureDate.diff(dob, "years");
  return age >= 0 ? age : 0;
};

export const convertAgeToYearByClient = (age, client, month) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let monthOfDob = dob.format("M");
  let monthNumber = monthToInteger(month ? month : "Jan");
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(monthNumber)) {
    adjustedYear = 1;
  }

  return dob.add(parseInt(age) + adjustedYear, "years").year();
};

export const convertKeyDateToDateByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let year = keyDate.year;
  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  let baseDate = moment(
    `${MONTH_NUM_MAP[keyDate.month]}-01-${year}`,
    "MM-DD-YYYY"
  );
  switch (keyDate.keyDateType.key) {
    case "RETIREMENT_AGE":
      return baseDate.add(keyDate.relativeAge + adjustedYear, "years");
    case "LIFE_EXPECTANCY":
      return baseDate.add(keyDate.relativeAge + adjustedYear, "years");
    default:
      return baseDate;
  }
};

export const getSelectedClientAppliesToType = (
  appliesTo,
  clientInformation
) => {
  const selectedClient = getSelectedClient(appliesTo, clientInformation);

  if (selectedClient === null) return "HOUSEHOLD";

  let relationshipType = selectedClient.relationshipType;

  if (selectedClient.relationshipType.hasOwnProperty("id")) {
    relationshipType = relationshipType.id;
  }

  if (relationshipType === "CLIENT") {
    return "HOUSEHOLD";
  } else {
    return "RELATION";
  }
};

export const mapBEKeyDateTypeToFEKeyDate = (keyDateType) => {
  if (keyDateType === "REGULAR_DATE")
    return {
      id: 0,
      key: "MANUAL_DATE",
    };

  if (keyDateType === "RETIREMENT_DATE")
    return {
      id: 1,
      key: "RETIREMENT_AGE",
    };

  return {
    id: 2,
    key: "LIFE_EXPECTANCY",
  };
};

export const mapFEKeyDateToBEDate = (keyDateType) => {
  switch (keyDateType) {
    case "MANUAL_DATE":
      return "REGULAR_DATE";
    case "RETIREMENT_AGE":
      return "RETIREMENT_DATE";
    case "LIFE_EXPECTANCY":
      return "MORTALITY_DATE";
    default:
      return "REGULAR_DATE";
  }
};

export const truncateString = (text, length) => {
  const substringLength = Math.min(text.length, length);
  const substring = text.substring(0, substringLength);
  const ellipses = substringLength < text.length ? "..." : "";
  return substring + ellipses;
};

export const getDefaultInflationRate = (defaultInflation, inflationType) => {
  if (inflationType === undefined || inflationType === null)
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  if (defaultInflation.isSystemWide)
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  if (!defaultInflation.hasOwnProperty(inflationType))
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  return defaultInflation[inflationType];
};

export const GOOD_COLOR = "#8bc53F";
export const WARNING_COLOR = "#FFbc3F";
export const FAIL_COLOR = "#F00";
export const GREY_COLOR = "#ccc";

export const DEFAULT_HIGH = 0.9;
export const DEFAULT_LOW = 0.5;

export const getGoalColorCode = (value, tracking) => {
  const min_value = tracking?.low ? tracking.low / 100 : DEFAULT_LOW;
  const max_value = tracking?.high ? tracking.high / 100 : DEFAULT_HIGH;

  if (value < min_value) return FAIL_COLOR;
  if (value > max_value) return WARNING_COLOR;
  return GOOD_COLOR;
};

export const createGradient = (color, canvas) => {
  const ctx = canvas.getContext("2d");

  const grad = ctx.createLinearGradient(0, 0, 0, 300);
  grad.addColorStop(0, alpha(GOOD_COLOR, 0.0875));
  grad.addColorStop(1, "rgba(0, 0, 0, 0)");

  return grad;
};

export const abbreviateCurrency = (value) => {
  let prefix = "";
  if (value < 0) {
    prefix = "-";
    value *= -1;
  }

  if (value >= 1000000000) {
    return `$${prefix}${(value / 1000000000).toFixed(0)}B`;
  }

  if (value >= 1000000) {
    return `$${prefix}${(value / 1000000).toFixed(0)}M`;
  }

  if (value >= 1000) {
    return `$${prefix}${(value / 1000).toFixed(0)}K`;
  }

  return `$${prefix}${value.toFixed(0)}`;
};

export const getChartDescription = (props, defaultWidth, defaultHeight) => {
  const margin = props.margin
    ? props.margin
    : {
        top: 10,
        bottom: 60,
        left: 60,
        right: 0,
      };

  const width = props.width ? props.width : defaultWidth;
  const height = props.height ? props.height : defaultHeight;

  const xMax = (width - margin.left - margin.right) * 0.95;
  const yMax = height - margin.top - margin.bottom;

  return {
    margin: margin,
    width: width,
    height: height,
    yMax: yMax,
    xMax: xMax,
  };
};

export const dateToMMDDYY = (date) => {
  const d = date.getDate();
  const m = date.getMonth() + 1; //Month from 0 to 11
  const y = date.getFullYear();
  return "" + (m <= 9 ? "0" + m : m) + "/" + (d <= 9 ? "0" + d : d) + "/" + y;
};

export const getCurrentYear = () => {
  return new Date().getFullYear();
};

export const getCurrentMonth = () => {
  return new Date().getMonth();
};

export const keyDateTransform = (clientInformation) => (item) => {
  item.startDate = {
    keyDateType: mapBEKeyDateTypeToFEKeyDate(item.date.startDateType),
    month: item.date.startMonth ? MONTH_MAP[item.date.startMonth - 1] : "Jan",
    year: item.date.startYear,
    age: convertYearToAgeByClient(
      item.startYear,
      getSelectedClient({ id: 0 }, clientInformation),
      "Jan"
    ),
    relativeAge: item.date.relativeStartDateNum,
  };
  item.endDate = {
    keyDateType: mapBEKeyDateTypeToFEKeyDate(item.date.endDateType),
    month: item.date.endMonth ? MONTH_MAP[item.date.endMonth - 1] : "Dec",
    year: item.date.endYear,
    age: convertYearToAgeByClient(
      item.endYear,
      getSelectedClient({ id: 0 }, clientInformation),
      "Dec"
    ),
    relativeAge: item.date.relativeEndDateNum,
  };

  item.startYear = item.startDate.year;
  item.startAge = convertYearToAgeByClient(
    item.startYear,
    getSelectedClient({ id: 0 }, clientInformation),
    "Jan"
  );

  item.endYear = item.endDate.year;
  item.endAge = convertYearToAgeByClient(
    item.endYear,
    getSelectedClient({ id: 0 }, clientInformation),
    "Dec"
  );

  return item;
};

export const taxPeriodTransform = (clientInformation) => (item, index) => {
  item.taxRate /= 100;

  item.startYear = item.date.startYear;
  item.startAge = convertYearToAgeByClient(
    item.startYear,
    getSelectedClient({ id: 0 }, clientInformation),
    "Jan"
  );

  item.endYear = item.date.endYear;
  item.endAge = convertYearToAgeByClient(
    item.endYear,
    getSelectedClient({ id: 0 }, clientInformation),
    "Dec"
  );

  item.inEdit = true;
  item.editId = index;

  return keyDateTransform(clientInformation)(item);
};

export const replaceNaNWithZero = (value) => {
  if (value === null) return 0;
  return !isNaN(value) ? value : 0;
};

export const calculateDataGridItemAges = (dataObject, clientInformation) => {
  const startDate = dataObject.start
    ? dataObject.start
    : new Date(dataObject.startYear, 0, 1);
  const endDate = dataObject.end
    ? dataObject.end
    : new Date(dataObject.endYear, 0, 1);
  const appliesToId = dataObject.appliesTo ? dataObject.appliesTo.id : 0;

  dataObject.startAge = convertYearToAgeByClient(
    startDate.getFullYear(),
    getSelectedClient(
      {
        id: appliesToId,
      },
      clientInformation
    ),
    dataObject.startMonth
  );

  dataObject.endAge = convertYearToAgeByClient(
    endDate.getFullYear(),
    getSelectedClient(
      {
        id: appliesToId,
      },
      clientInformation
    ),
    dataObject.endMonth
  );

  if (dataObject.date) {
    dataObject.date = {
      ...dataObject.date,
      startAge: dataObject.startAge,
      start: startDate,
      endAge: dataObject.endAge,
      end: endDate,
      appliesTo: { ...dataObject.appliesTo },
    };
  }

  return dataObject;
};

export const getYearAngAgeByClientAndAge = (month, selectedClient, type) => {
  const currentYear = getCurrentYear();
  const currentMonth = getCurrentMonth();
  let monthString;
  let monthNumber;
  if (month) {
    if (Number.isInteger(month)) {
      monthString = MONTH_LABEL_MAP[month];
      monthNumber = month;
    } else {
      monthString = month;
      monthNumber = MONTH_NUM_MAP[month];
    }
  } else {
    monthString = MONTH_LABEL_MAP[parseInt(selectedClient.dateOfBirth.split("/")[0])];
    monthNumber = parseInt(selectedClient.dateOfBirth.split("/")[0]);
  }
  const currentAge = convertYearToAgeByClient(
    currentYear,
    selectedClient,
    monthString
  );

  let comparedAge =
    type === "retirement"
      ? selectedClient.retirementAge
      : selectedClient.lifeExpectancyAge;
  if (currentAge >= comparedAge) {
    return [currentYear, currentAge, currentMonth];
  } else {
    return [
      convertKeyDateToYearByClient(
        {
          age:
            type === "retirement"
              ? selectedClient.retirementAge
              : selectedClient.lifeExpectancyAge,
          month: monthNumber,
        },
        selectedClient
      ),
      comparedAge,
      0,
    ];
  }
};

export const updateStartYearOrEndYear = (
  event,
  dataObject,
  clientInformation
) => {
  const field = event.field;
  dataObject.isEdited = true;

  const date = event.value;
  dataObject[event.field] = event.value;

  if (date === null) return dataObject;

  const appliesToId = dataObject.appliesTo ? dataObject.appliesTo.id : 0;

  if (field === "start") {
    dataObject.startYear = date.getFullYear();
    dataObject.startMonth = MONTH_MAP[date.getMonth()];
    dataObject.startAge = convertYearToAgeByClient(
      date.getFullYear(),
      getSelectedClient({ id: appliesToId }, clientInformation),
      dataObject.startMonth ? dataObject.startMonth : "Jan"
    );
  }

  if (field === "end") {
    dataObject.endYear = date.getFullYear();
    dataObject.endMonth = MONTH_MAP[date.getMonth()];
    dataObject.endAge = convertYearToAgeByClient(
      date.getFullYear(),
      getSelectedClient({ id: appliesToId }, clientInformation),
      dataObject.endMonth ? dataObject.endMonth : "Jan"
    );
  }
/*  dataObject.startDateValid = !isDatePriorToTodaysMonth(dataObject.start);

  let lifeExpectancyAge = clientInformation.client.lifeExpectancyAge;

  if (clientInformation.client.coClient) {
    lifeExpectancyAge = clientInformation.client.coClient.lifeExpectancyAge > clientInformation.client.lifeExpectancyAge ? clientInformation.coClient.lifeExpectancyAge : lifeExpectancyAge;
  }

  dataObject.endDateValid = !isDateFurtherThanLifeExpectancy(
    dataObject.end,
    clientInformation.client.dateOfBirth,
    lifeExpectancyAge
  );*/

  return dataObject;
};

export const updateStartAgeOrEndAge = (
  event,
  dataObject,
  clientInformation
) => {
  const field = event.field;
  const yearField = field.replace("Age", "Year");
  const monthField = event.field.includes("start") ? "startMonth" : "endMonth";
  const appliesToId = dataObject.appliesTo ? dataObject.appliesTo.id : 0;

  dataObject.isEdited = true;

  const month = dataObject[monthField]
    ? MONTH_NUM_MAP[dataObject[monthField]] - 1
    : 0;

  const ageValue = Math.min(event.value, 200);

  if (field === "startAge") {
    dataObject.startYear = convertAgeToYearByClient(
      ageValue,
      getSelectedClient({ id: appliesToId }, clientInformation),
      dataObject[monthField] ? dataObject[monthField] : "Jan"
    );
    dataObject.startAge = replaceNaNWithZero(parseInt(ageValue));
    dataObject.start = new Date(dataObject.startYear, month, 1);
  } else if (field === "endAge") {
    dataObject.endYear = convertAgeToYearByClient(
      ageValue,
      getSelectedClient({ id: appliesToId }, clientInformation),
      dataObject[monthField] ? dataObject[monthField] : "Dec"
    );
    dataObject.endAge = replaceNaNWithZero(parseInt(ageValue));
    dataObject.end = new Date(dataObject.endYear, month, 1);
  }
  return dataObject;
};

export const convertToDateByClient = (clientInformation, adjustedYears) => {
  let dob = moment(clientInformation["dateOfBirth"], "MM/DD/YYYY");
  dob.add(adjustedYears, "years");
  return dob.toDate();
};

export const calculateFutureCost = (item) => {
  const currentYear = parseInt(new Date().getFullYear());

  let futureValue = 0;
  for (let year = item.startYear; year < item.endYear + 1; year++) {
    futureValue +=
      item.amount * Math.pow(1.0 + item.inflationRate, year - currentYear);
  }

  return futureValue;
};

export const focusFirstEditItemInput = (parentElement) => {
  const editRows = parentElement.getElementsByClassName("k-grid-edit-row");

  const lastNewItem = editRows[editRows.length - 1];

  if (!lastNewItem) return;

  const firstInput = lastNewItem.getElementsByClassName(
    "MuiInputBase-input"
  )[0];
  firstInput.focus();
};

export const updateKeyDate = (
  item,
  field,
  retirementDate,
  mortalityDate,
  prefix,
  client
) => {
  // case 1: Manual Date
  const updateManualDate = () => {
    if (field === prefix) {
      // case 1a: Year / Month field was updated.
      item[`${prefix}Age`] = convertYearToAgeByClient(
        item[prefix].getFullYear(),
        getSelectedClient({ id: item.appliesTo.id }, client),
        MONTH_MAP[item[prefix].getMonth()]
      );
      return;
    }

    if (field.includes("Age")) {
      // case 1b: Age field was updated.
      const year = convertAgeToYearByClient(
        item[field],
        getSelectedClient({ id: item.appliesTo.id }, client),
        MONTH_MAP[item[prefix].getMonth()]
      );
      item[prefix] = new Date(year, item[prefix].getMonth(), 1);
    }

    // case 1c: keyDateType was updated.
    item[prefix] = new Date();
    item[`${prefix}Age`] = convertYearToAgeByClient(
      item[prefix].getFullYear(),
      getSelectedClient({ id: item.appliesTo.id }, client),
      MONTH_MAP[item[prefix].getMonth()]
    );
  };

  // case 2: Retirement Date
  const updateRetirementDate = () => {
    if (field.includes("RelativeDateNum")) {
      // case 2a: Relative Date Num was updated.

      const retirementYear = retirementDate.getFullYear();

      item[prefix] = new Date(
        retirementYear + item[field],
        item[prefix].getMonth(),
        1
      );
      item[`${prefix}Age`] = convertYearToAgeByClient(
        item[prefix].getFullYear(),
        getSelectedClient({ id: item.appliesTo.id }, client),
        MONTH_MAP[item[prefix].getMonth()]
      );

      return;
    }

    // case 2b: KeyDate Type was updated.
    item[prefix] = retirementDate;
    item[`${prefix}RelativeDateNum`] = 0;
    item[`${prefix}Age`] = convertYearToAgeByClient(
      item[prefix].getFullYear(),
      getSelectedClient({ id: item.appliesTo.id }, client),
      MONTH_MAP[item[prefix].getMonth()]
    );
  };

  // case 3: Mortality Date
  const updateMortalityDate = () => {
    if (field.includes("RelativeDateNum")) {
      // case 3a: Relative Date Num was updated.
      const mortalityYear = mortalityDate.getFullYear();

      item[prefix] = new Date(
        mortalityYear + item[field],
        item[prefix].getMonth(),
        1
      );
      item[`${prefix}Age`] = convertYearToAgeByClient(
        item[prefix].getFullYear(),
        getSelectedClient({ id: item.appliesTo.id }, client),
        MONTH_MAP[item[prefix].getMonth()]
      );

      return;
    }
    // case 3b: KeyDateType was updated.
    item[prefix] = mortalityDate;
    item[`${prefix}RelativeDateNum`] = 0;
    item[`${prefix}Age`] = convertYearToAgeByClient(
      item[prefix].getFullYear(),
      getSelectedClient({ id: item.appliesTo.id }, client),
      MONTH_MAP[item[prefix].getMonth()]
    );
  };

  const keyDateType = item[`${prefix}KeyDateType`];

  switch (keyDateType) {
    case REGULAR_DATE:
      updateManualDate();
      break;
    case RETIREMENT_DATE:
      updateRetirementDate();
      break;
    case MORTALITY_DATE:
      updateMortalityDate();
      break;
    default:
      throw `Invalid Key Date Category: ${keyDateType}`;
  }

  return { ...item };
};

export const formatDateToMonthYear = (d) => {
  const year = d.getFullYear();
  const month = MONTH_MAP[d.getMonth()];

  return `${month}-${year}`;
};

export const isFieldLocked = (dataItem, field) => {
  if (!dataItem.lockedFields) return false;
  return dataItem.lockedFields.includes(field);
};

export const getEarliestPossibleRetirementDate = (selectedClient) => {
  const dob = moment(selectedClient.dateOfBirth, "MM/DD/YYYY");
  const retirementDate = dob.add(selectedClient.retirementAge, "years").toDate();
  if (retirementDate.getFullYear() < getCurrentYear()) {
    return new Date(`${getCurrentYear()}-${getCurrentMonth()+1}-1`);
  } else {
    return retirementDate;
  }
};

export const getLatestPossibleDeathDate = (selectedClient) => {
  const dob = moment(selectedClient.dateOfBirth, "MM/DD/YYYY");
  return dob.add(selectedClient.lifeExpectancyAge, "years").add(11, "months").toDate();
};